import { useEffect, useState } from "react";

import { IconPlus } from "@/components/Icons";
import {
  Button,
  Card,
  Checkbox,
  CheckboxField,
  Crumbs,
  Empty,
  Field,
  FieldRow,
  Filters,
  Input,
  Kbd,
  PageHead,
  Pill,
  SearchField,
  Section,
  Segmented,
  Select,
  Spacer,
  Stack,
  Table,
  Td,
  Textarea,
  Th,
  Toolbar,
  type AppliedFilter,
  type FilterField,
} from "@/components/kit";

import styles from "./DesignPage.module.css";

/**
 * Grounds to choose between.
 *
 * The ground is not one colour — changing it drags the hover, the table head
 * and the hairlines with it, or the page comes apart. So each option is a set,
 * and they are swapped together.
 *
 * The thing to judge: how clearly a white table separates from the page behind
 * it. The whole design leans on that, and "paper" barely manages it.
 */
const GROUNDS: Record<
  string,
  { label: string; note: string; vars: Record<string, string> }
> = {
  paper: {
    label: "Paper",
    note: "Warm and very quiet. White cards barely separate from the page — that is the weakness.",
    vars: {
      "--ground": "#faf9f7",
      "--surface-2": "#f6f5f2",
      "--surface-3": "#efedea",
      "--line": "#e7e4df",
      "--line-2": "#d6d2cb",
    },
  },
  linen: {
    label: "Linen",
    note: "The same warmth, taken far enough that a white table clearly sits on top of the page.",
    vars: {
      "--ground": "#f2efe9",
      "--surface-2": "#faf8f5",
      "--surface-3": "#f1ede6",
      "--line": "#e2ddd3",
      "--line-2": "#d0c9bc",
    },
  },
  neutral: {
    label: "Neutral",
    note: "No cast at all. Safest, and the least opinionated of the five.",
    vars: {
      "--ground": "#f4f4f5",
      "--surface-2": "#fafafa",
      "--surface-3": "#f0f0f1",
      "--line": "#e4e4e7",
      "--line-2": "#d1d1d6",
    },
  },
  slate: {
    label: "Slate",
    note: "Chosen. Cool blue-grey, familiar on sight, and it puts enough distance under a white table for one to sit clearly on top of the page.",
    vars: {
      "--ground": "#f3f5f9",
      "--surface-2": "#f8fafc",
      "--surface-3": "#edf1f7",
      "--line": "#e2e8f0",
      "--line-2": "#cbd5e1",
    },
  },
  white: {
    label: "White",
    note: "No ground at all. Hairlines do every bit of the separating. Crispest, and the least forgiving of a missing border.",
    vars: {
      "--ground": "#ffffff",
      "--surface-2": "#f7f7f6",
      "--surface-3": "#f2f1ef",
      "--line": "#e8e6e2",
      "--line-2": "#d7d3cc",
    },
  },
};

/**
 * Table heads to choose between.
 *
 * The one band on the page whose weight is genuinely a taste call. A rule, a
 * tint and a dark bar all work; they just say different things about what kind
 * of product this is. Judge it on the players table, which is the one people
 * will stare at for hours.
 */
const HEADS: Record<string, { label: string; note: string; vars: Record<string, string> }> = {
  ink: {
    label: "Ink",
    note: "A dark bar. Unmistakably the header, holds its own against a long table, and the strongest signal that this is a data tool rather than a form.",
    vars: { "--thead-bg": "#1e2532", "--thead-fg": "#cbd5e1", "--thead-line": "#1e2532" },
  },
  graphite: {
    label: "Graphite",
    note: "Chosen. Dark enough to be unmistakably the header and to hold a long table together, light enough not to dominate three rows.",
    vars: { "--thead-bg": "#475569", "--thead-fg": "#e2e8f0", "--thead-line": "#475569" },
  },
  soft: {
    label: "Soft",
    note: "A plain grey band with no blue in it — the tint without the cast you did not like.",
    vars: { "--thead-bg": "#f1f2f4", "--thead-fg": "#4b5563", "--thead-line": "#dfe3e8" },
  },
  ground: {
    label: "Ground",
    note: "The page colour, as if the sheet were cut away at the top. Quietest of the five.",
    vars: { "--thead-bg": "#f3f5f9", "--thead-fg": "#4b5563", "--thead-line": "#e2e8f0" },
  },
  white: {
    label: "White",
    note: "No fill at all — a rule and small capitals do the work. Cleanest, and the one you rejected.",
    vars: { "--thead-bg": "#ffffff", "--thead-fg": "#4b5563", "--thead-line": "#cbd5e1" },
  },
};

const COLOURS = [
  ["--ground", "#f3f5f9", "the page"],
  ["--surface", "#ffffff", "cards, tables, inputs"],
  ["--surface-2", "#f8fafc", "hover, zebra"],
  ["--surface-3", "#edf1f7", "wells, quiet pills"],
  ["--ink", "#16181d", "text"],
  ["--ink-2", "#4b5563", "secondary text"],
  ["--ink-3", "#8a93a3", "hints, placeholders"],
  ["--line", "#e2e8f0", "hairlines"],
  ["--line-2", "#cbd5e1", "input borders, under a table head"],
  ["--accent", "#1a56db", "main action, focus, selection, links"],
  ["--live", "#c2410c", "happening now"],
  ["--good", "#15803d", "done, published"],
  ["--warn", "#a16207", "needs attention"],
  ["--bad", "#b42318", "wrong, failed"],
] as const;

const TYPE = [
  ["--t-2xl / 29px", "var(--t-2xl)", "600", "Big numbers, rarely"],
  ["--t-xl / 21px", "var(--t-xl)", "600", "Page titles"],
  ["--t-lg / 16.5px", "var(--t-lg)", "600", "Section titles"],
  ["--t-md / 14.5px", "var(--t-md)", "400", "Body, inputs, buttons"],
  ["--t-sm / 13px", "var(--t-sm)", "400", "Dense table text"],
  ["--t-xs / 11.5px", "var(--t-xs)", "500", "LABELS AND TABLE HEADS"],
] as const;

/* A sport's own declared details, which is all the filter bar ever gets to
   know. Cricket declared these; swimming would declare something else, and the
   same bar has to work for both. */
const PLAYER_FIELDS: FilterField[] = [
  { key: "country", label: "Country", kind: "choice", options: ["India", "Australia", "England"] },
  {
    key: "batting",
    label: "Batting",
    kind: "choice",
    options: ["Right hand", "Left hand"],
  },
  { key: "bowling", label: "Bowling", kind: "text" },
  { key: "born", label: "Born", kind: "number" },
];

const PLAYERS = [
  { name: "Rohit Sharma", country: "India", batting: "Right hand", bowling: "Right arm off break", born: 1987 },
  { name: "Jasprit Bumrah", country: "India", batting: "Right hand", bowling: "Right arm fast", born: 1993 },
  { name: "Rishabh Pant", country: "India", batting: "Left hand", bowling: "—", born: 1997 },
  { name: "Pat Cummins", country: "Australia", batting: "Right hand", bowling: "Right arm fast", born: 1993 },
  { name: "Mitchell Starc", country: "Australia", batting: "Left hand", bowling: "Left arm fast", born: 1990 },
  { name: "Joe Root", country: "England", batting: "Right hand", bowling: "Right arm off break", born: 1990 },
];

/** Applies the chips to the rows. Real enough to prove the bar works — the
 * live screen asks the server, which is why the shape of a filter has to be
 * this plain. */
function matches(row: (typeof PLAYERS)[number], filter: AppliedFilter): boolean {
  const raw = row[filter.key as keyof typeof row];
  const cell = String(raw).toLowerCase();
  const want = filter.value.toLowerCase();
  if (filter.test === "is empty") return cell === "" || cell === "—";
  if (want === "") return true;
  switch (filter.test) {
    case "is":
      return cell === want;
    case "is not":
      return cell !== want;
    case "contains":
      return cell.includes(want);
    case "at least":
      return Number(raw) >= Number(filter.value);
    case "at most":
      return Number(raw) <= Number(filter.value);
    default:
      return true;
  }
}

const FIXTURES = [
  ["IND v AUS", "Super Eights", "T20", "live", "Live"],
  ["ENG v SA", "Super Eights", "T20", "neutral", "Scheduled"],
  ["NZ v PAK", "Group B", "ODI", "good", "Final"],
  ["SL v BAN", "Group B", "Test", "warn", "Delayed"],
] as const;

/**
 * The shared parts, rendered.
 *
 * Two jobs. The first half is the system itself — every colour, size and
 * control in one place, so drift is visible the moment it starts. The second
 * half is two real screen slices built only from those parts, because a kit
 * that looks fine as a row of buttons can still make a bad screen, and that is
 * the thing actually worth judging.
 */
export function DesignPage() {
  const [outcome, setOutcome] = useState("field");
  const [invalid, setInvalid] = useState(true);
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [query, setQuery] = useState("");
  const [ground, setGround] = useState("slate");
  const [head, setHead] = useState("graphite");

  // Written straight onto the root so the swap is the real thing — the same
  // tokens every component already reads, not a preview that could differ.
  useEffect(() => {
    const root = document.documentElement;
    const chosen = { ...GROUNDS[ground].vars, ...HEADS[head].vars };
    for (const [name, value] of Object.entries(chosen)) root.style.setProperty(name, value);
    return () => {
      for (const name of Object.keys(chosen)) root.style.removeProperty(name);
    };
  }, [ground, head]);

  const shown = PLAYERS.filter(
    (row) =>
      row.name.toLowerCase().includes(query.toLowerCase()) &&
      filters.every((filter) => matches(row, filter)),
  );

  return (
    <div className={styles.page}>
      <PageHead
        title="The shared parts"
        note="Everything the new screens are built from. Nothing outside this page is allowed to invent a colour or a text size."
      />

      <Section
        title="The ground"
        note="Slate is now the system. The others are kept switchable so the choice can be re-argued against real content rather than from memory — watch the players table as you flip."
      >
        <div className={styles.stack}>
          <Segmented
            value={ground}
            onChange={setGround}
            name="Ground"
            options={Object.entries(GROUNDS).map(([value, g]) => ({ value, label: g.label }))}
          />
          <p className={styles.swatchHex}>{GROUNDS[ground].note}</p>
        </div>
      </Section>

      <Section
        title="The table head"
        note="Five weights for the same band. Judge them on the players table at the bottom — that is the one people stare at for hours, not the short one here."
      >
        <div className={styles.stack}>
          <Segmented
            value={head}
            onChange={setHead}
            name="Table head"
            options={Object.entries(HEADS).map(([value, h]) => ({ value, label: h.label }))}
          />
          <p className={styles.swatchHex}>{HEADS[head].note}</p>
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Country</Th>
                <Th num>Born</Th>
              </tr>
            </thead>
            <tbody>
              {PLAYERS.slice(0, 3).map((row) => (
                <tr key={row.name}>
                  <Td strong>{row.name}</Td>
                  <Td>{row.country}</Td>
                  <Td num>{row.born}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Section>

      <Section
        title="Colour"
        note="One blue carries every interactive meaning — the main action, focus, selection, links. Nothing decorative is ever this blue, so anything blue can be acted on."
      >
        <div className={styles.swatches}>
          {COLOURS.map(([name, hex, use]) => (
            <div key={name} className={styles.swatch}>
              <div className={styles.chip} style={{ background: `var(${name})` }} />
              <span className={styles.swatchName}>{name}</span>
              <span className={styles.swatchHex}>
                {hex} · {use}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Type"
        note="Six sizes. The old surface had thirteen, six of them between 10 and 15px — which is how a screen stops looking designed. These sit half a pixel above the first pass: that one was too tight to read all day, a full pixel started to look like a website."
      >
        {TYPE.map(([name, size, weight, sample]) => (
          <div key={name} className={styles.specimen}>
            <span className={styles.specimenName}>{name}</span>
            <span style={{ fontSize: size, fontWeight: Number(weight) }}>{sample}</span>
          </div>
        ))}
      </Section>

      <Section
        title="Buttons"
        note="Primary is ink, not blue. A black button is unmistakably the main action, and it leaves blue free to mean focus."
      >
        <div className={styles.stack}>
          <div className={styles.row}>
            <Button tone="primary">Publish</Button>
            <Button>Cancel</Button>
            <Button tone="subtle">Skip</Button>
            <Button tone="danger">Delete</Button>
            <Button tone="primary" disabled>
              Disabled
            </Button>
          </div>
          <div className={styles.row}>
            <Button tone="primary" size="lg">
              Large
            </Button>
            <Button icon aria-label="Add">
              <IconPlus size={15} />
            </Button>
            <Button tone="primary">
              <IconPlus size={15} />
              With an icon
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="Fields"
        note="An error replaces the hint rather than appearing under it, so a form never grows and pushes itself down as you type."
      >
        <div className={styles.stack}>
          <FieldRow>
            <Field label="Name" required hint="As it should appear everywhere" htmlFor="d-name">
              <Input id="d-name" defaultValue="Mumbai Indians" />
            </Field>
            <Field label="Country" htmlFor="d-country">
              <Select id="d-country" defaultValue="IND">
                <option value="IND">India</option>
                <option value="AUS">Australia</option>
              </Select>
            </Field>
            <Field
              label="Short code"
              error={invalid ? "Already used by another club" : null}
              hint="Three letters"
              htmlFor="d-code"
            >
              <Input id="d-code" defaultValue="MI" aria-invalid={invalid} />
            </Field>
          </FieldRow>
          <Field label="Notes" htmlFor="d-notes">
            <Textarea id="d-notes" placeholder="Anything worth recording" />
          </Field>
          <div className={styles.row}>
            <SearchField placeholder="Search players" />
            <Segmented
              value={outcome}
              onChange={setOutcome}
              name="How it is decided"
              options={[
                { value: "duel", label: "Two sides" },
                { value: "field", label: "A field" },
                { value: "series", label: "Attempts" },
                { value: "composite", label: "Made of matches" },
              ]}
            />
            <Checkbox
              label="Show the error state"
              checked={invalid}
              onChange={(e) => setInvalid(e.target.checked)}
            />
          </div>
          <p className={styles.swatchHex}>
            Press <Kbd>Tab</Kbd> through the row above — every control shows the same focus ring,
            and it is never removed.
          </p>

          {/* The one that is easy to get wrong. A bare checkbox among labelled
              fields lands on the *label* line and floats above the inputs; this
              borrows the field's structure so it lines up by construction. */}
          <FieldRow>
            <Field label="Name" htmlFor="d-check-name">
              <Input id="d-check-name" placeholder="Batting style" />
            </Field>
            <Field label="Kind" htmlFor="d-check-kind">
              <Select id="d-check-kind" defaultValue="text">
                <option value="text">Text</option>
                <option value="integer">Whole number</option>
              </Select>
            </Field>
            <CheckboxField label="Required" />
          </FieldRow>
          <p className={styles.swatchHex}>
            <code>CheckboxField</code> sits on the control line. A plain{" "}
            <code>Checkbox</code> in that row would sit level with “Name” and “Kind”.
          </p>
        </div>
      </Section>

      <Section
        title="State"
        note="Colour is never the only carrier. Anything that matters gets a dot as well, so it survives a screenshot and a person who cannot tell the two apart."
      >
        <div className={styles.row}>
          <Pill tone="live" dot>
            Live
          </Pill>
          <Pill tone="good" dot>
            Published
          </Pill>
          <Pill tone="warn" dot>
            Needs attention
          </Pill>
          <Pill tone="bad" dot>
            Failed
          </Pill>
          <Pill tone="accent">T20</Pill>
          <Pill>Draft</Pill>
        </div>
      </Section>

      <Section title="Tables" note="Dense rows, hairline rules, numbers that line up.">
        <Table>
          <thead>
            <tr>
              <Th>Fixture</Th>
              <Th>Where it sits</Th>
              <Th>Config</Th>
              <Th>Status</Th>
              <Th num>Entries</Th>
            </tr>
          </thead>
          <tbody>
            {FIXTURES.map(([name, where, config, tone, status]) => (
              <tr key={name}>
                <Td strong>{name}</Td>
                <Td quiet>{where}</Td>
                <Td>
                  <Pill tone="accent">{config}</Pill>
                </Td>
                <Td>
                  <Pill tone={tone} dot={tone !== "neutral"}>
                    {status}
                  </Pill>
                </Td>
                <Td num>22</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section
        title="Nothing there yet"
        note="An empty list always carries the thing that fills it. An empty screen with no way forward is the commonest dead end there is."
      >
        <Card>
          <Empty
            title="No venues yet"
            note="Venues are shared, so one Wankhede Stadium is used by every sport that plays there."
            action={
              <Button tone="primary">
                <IconPlus size={15} />
                Add a venue
              </Button>
            }
          />
        </Card>
      </Section>

      <Section
        title="A real screen — part of the sport editor"
        note="Built only from the parts above. This is the test: a kit can look fine as a row of buttons and still make a bad screen."
      >
        <div className={styles.mock}>
          <div className={styles.mockBar}>omnium · cricket · setup</div>
          <div className={styles.mockBody}>
            <PageHead
              title="Cricket"
              note="Saves as you type. Publishing is separate."
              actions={
                <div className={styles.row}>
                  <Pill tone="good" dot>
                    Saved
                  </Pill>
                  <Button tone="primary">Publish</Button>
                </div>
              }
            />

            <Section title="Name it">
              <FieldRow>
                <Field label="Code" required htmlFor="m-code">
                  <Input id="m-code" defaultValue="cricket" />
                </Field>
                <Field label="Name" required htmlFor="m-name">
                  <Input id="m-name" defaultValue="Cricket" />
                </Field>
                <Field label="Sits inside" hint="Leave empty for a top-level sport" htmlFor="m-in">
                  <Select id="m-in" defaultValue="">
                    <option value="">—</option>
                    <option value="aquatics">Aquatics</option>
                  </Select>
                </Field>
              </FieldRow>
            </Section>

            <Section
              title="What it keeps"
              note="In the order you add them — that is the order they appear on every screen."
              actions={
                <Button>
                  <IconPlus size={15} />
                  Add
                </Button>
              }
            >
              <div className={styles.things}>
                {[
                  ["Team", "name · short code · country · founded", "cricket"],
                  ["Player", "name · born · batting style · bowling style", "cricket"],
                  ["Umpire", "name · country", "cricket"],
                  ["Country", "name · three-letter code", "every sport"],
                  ["Ground", "name · city · capacity", "every sport"],
                ].map(([name, detail, scope]) => (
                  <div key={name} className={styles.thing}>
                    <span className={styles.thingName}>{name}</span>
                    <span className={styles.thingDetail}>{detail}</span>
                    <Pill tone={scope === "cricket" ? "neutral" : "accent"}>{scope}</Pill>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title="Configs"
              note="Same shape, different rules. Picked in one step when a fixture is added."
              actions={
                <Button>
                  <IconPlus size={15} />
                  Add
                </Button>
              }
            >
              <div className={styles.things}>
                {[
                  ["T20", "20 overs · 6 balls · 1 innings each · 1 day"],
                  ["ODI", "50 overs · 6 balls · 1 innings each · 1 day"],
                  ["Test", "6 balls · 2 innings each · 5 days"],
                  ["The Hundred", "100 balls · sets of 5 · 1 innings each · 1 day"],
                ].map(([name, detail]) => (
                  <div key={name} className={styles.thing}>
                    <span className={styles.thingName}>{name}</span>
                    <span className={styles.thingDetail}>{detail}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </Section>

      <Section
        title="A real screen — a data list, with filters"
        note="Try the filters — they work. We never know what a sport's details are, so a fixed filter panel cannot be designed. What we do know is that every filter is the same three parts: a field, a test, a value. So that is what gets designed, and the sport supplies the rest."
      >
        <div className={styles.mock}>
          <div className={styles.mockBar}>omnium · cricket · data</div>
          <div className={styles.mockBody}>
            <Crumbs
              trail={[{ label: "Sports" }, { label: "Cricket" }, { label: "Players" }]}
            />
            <PageHead
              title="Players"
              note={
                filters.length || query
                  ? `${shown.length} of ${PLAYERS.length} shown`
                  : `${PLAYERS.length} records`
              }
              actions={
                <div className={styles.row}>
                  <Button>Import</Button>
                  <Button tone="primary">
                    <IconPlus size={15} />
                    Add a player
                  </Button>
                </div>
              }
            />
            <Stack>
            <Toolbar>
              <SearchField
                placeholder="Search players"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Filters fields={PLAYER_FIELDS} value={filters} onChange={setFilters} />
              <Spacer />
              <span className={styles.swatchHex}>
                Paste a column with <Kbd>⌘</Kbd>
                <Kbd>V</Kbd>
              </span>
            </Toolbar>
            </Stack>
            {shown.length === 0 ? (
              <Card>
                <Empty
                  title="No players match"
                  note="Remove a filter, or widen one."
                  action={
                    <Button onClick={() => { setFilters([]); setQuery(""); }}>Clear filters</Button>
                  }
                />
              </Card>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Country</Th>
                    <Th>Batting</Th>
                    <Th>Bowling</Th>
                    <Th num>Born</Th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr key={row.name}>
                      <Td strong>{row.name}</Td>
                      <Td>{row.country}</Td>
                      <Td quiet>{row.batting}</Td>
                      <Td quiet>{row.bowling}</Td>
                      <Td num>{row.born}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
